/**
 * /admin/courses/$courseId — one course: its details, whether coaches can see
 * it, and its weeks in order.
 *
 * Aliases: course editor, edit course admin, course weeks admin
 */

import { useStore } from "@tanstack/react-form";
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import {
  Link,
  createFileRoute,
  notFound,
  useNavigate,
} from "@tanstack/react-router";
import { ArrowDown, ArrowUp, ExternalLink } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { VideoThumbnail } from "~/components/admin/video-preview";
import { useAppForm } from "~/components/form/app-form";
import {
  FieldErrorsProvider,
  zodErrorLookup,
} from "~/components/form/fields/field-errors";
import { Button, ButtonLink } from "~/components/ui/button";
import { ConfirmDialog } from "~/components/ui/confirm-dialog";
import { Input } from "~/components/ui/input";
import {
  courseQueryOptions,
  createCourseWeekFn,
  deleteCourseFn,
  deleteCourseWeekFn,
  learningAdminKeys,
  reorderCourseWeeksFn,
  setCourseStatusFn,
  updateCourseDetailsFn,
  videosQueryOptions,
  type AdminCourse,
  type AdminCourseWeek,
  type DeleteCourseResult,
} from "~/lib/functions/admin-learning";
import { courseInputSchema } from "~/lib/shared/learning/admin-inputs";
import type { CourseStatus } from "~/lib/shared/learning/courses";
import { assetByKey } from "~/lib/shared/nova/assets/catalog";

import { CourseStatusBadge } from "../-components/course-status-badge";

type DeleteRefusalReason = Extract<
  DeleteCourseResult,
  { deleted: false }
>["reason"];

const DELETE_REFUSAL_MESSAGE = {
  published: "Unpublish the course before deleting it.",
  has_progress:
    "Coaches have progress in this course, so it cannot be deleted.",
} as const satisfies Record<DeleteRefusalReason, string>;

export const Route = createFileRoute("/admin/courses/$courseId/")({
  staticData: { title: "Course" },
  loader: async ({ context, params }) => {
    if (!z.string().uuid().safeParse(params.courseId).success) {
      throw notFound();
    }
    const [course] = await Promise.all([
      context.queryClient.ensureQueryData(courseQueryOptions(params.courseId)),
      context.queryClient.ensureQueryData(videosQueryOptions()),
    ]);
    return { title: course?.title ?? "Course" };
  },
  component: CourseEditorRoute,
});

function CourseEditorRoute() {
  const { courseId } = Route.useParams();
  const { data: course } = useSuspenseQuery(courseQueryOptions(courseId));
  if (!course) return null;
  return <CourseEditor course={course} />;
}

function useInvalidateLearning() {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: learningAdminKeys.all });
}

function CourseEditor({ course }: { course: AdminCourse }) {
  const invalidate = useInvalidateLearning();
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const setStatus = useMutation({
    mutationFn: (status: CourseStatus) =>
      setCourseStatusFn({ data: { id: course.id, status } }),
    onSuccess: async (_result, status) => {
      await invalidate();
      toast.success(
        status === "published"
          ? "Published. Coaches with the new view see it in their menu."
          : "Unpublished. Coaches no longer see it.",
      );
    },
    onError: () => toast.error("Couldn't change whether coaches see it."),
  });

  const remove = useMutation({
    mutationFn: () => deleteCourseFn({ data: { id: course.id } }),
    onSuccess: async (result) => {
      setConfirmDelete(false);
      if (!result.deleted) {
        toast.error(DELETE_REFUSAL_MESSAGE[result.reason]);
        return;
      }
      await invalidate();
      await navigate({ to: "/admin/courses" });
    },
    onError: () => toast.error("Couldn't delete the course."),
  });

  const published = course.status === "published";

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1.5">
          <h1 className="text-2xl font-medium text-gray-900">{course.title}</h1>
          <CourseStatusBadge status={course.status} />
        </div>
        <div className="flex flex-wrap gap-2">
          {published && (
            <ButtonLink
              href={`/my-coaching/business/courses/${course.slug}`}
              rel="noopener noreferrer"
              target="_blank"
            >
              View as a coach
              <ExternalLink aria-hidden="true" className="size-4" />
            </ButtonLink>
          )}
          <Button
            loading={setStatus.isPending}
            onClick={() => setStatus.mutate(published ? "draft" : "published")}
            variant={published ? "default" : "primary"}
          >
            {published ? "Unpublish" : "Publish"}
          </Button>
          {!published && (
            <Button
              onClick={() => setConfirmDelete(true)}
              variant="destructive-ghost"
            >
              Delete
            </Button>
          )}
        </div>
      </div>

      <CourseDetailsForm course={course} />
      <WeekList course={course} />

      <ConfirmDialog
        confirmLabel="Delete course"
        description="Its weeks and materials go with it. The videos stay in the library."
        isLoading={remove.isPending}
        onConfirm={() => remove.mutate()}
        onOpenChange={setConfirmDelete}
        open={confirmDelete}
        title={`Delete “${course.title}”?`}
        variant="destructive"
      />
    </div>
  );
}

function CourseDetailsForm({ course }: { course: AdminCourse }) {
  const invalidate = useInvalidateLearning();
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [takenSlug, setTakenSlug] = useState<string | null>(null);

  const form = useAppForm({
    defaultValues: {
      title: course.title,
      slug: course.slug,
      headline: course.headline,
      description: course.description,
    },
  });
  const values = useStore(form.store, (state) => state.values);
  const parsed = courseInputSchema.safeParse(values);
  const lookup = zodErrorLookup(
    parsed.success ? [] : parsed.error.issues,
    submitAttempted,
  );
  const errorFor = (name: string): string | undefined =>
    name === "slug" && takenSlug === values.slug.trim()
      ? "Another course already uses this address."
      : lookup(name);

  const save = useMutation({
    mutationFn: updateCourseDetailsFn,
    onSuccess: async (result, { data }) => {
      if (!result.saved) {
        setTakenSlug(data.slug);
        return;
      }
      await invalidate();
      toast.success("Course saved.");
    },
    onError: () => toast.error("Couldn't save the course."),
  });

  return (
    <section className="space-y-4 rounded-xl border border-gray-200 bg-white p-5">
      <h2 className="font-medium text-gray-900">Details</h2>
      <FieldErrorsProvider value={errorFor}>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            setSubmitAttempted(true);
            if (parsed.success) {
              save.mutate({
                data: { ...parsed.data, id: course.id },
              });
            }
          }}
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <form.AppField name="title">
              {(field) => (
                <field.TextField
                  helpText="The menu label coaches click."
                  label="Name"
                  required
                />
              )}
            </form.AppField>
            <form.AppField name="slug">
              {(field) => (
                <field.TextField
                  helpText={
                    course.status === "published"
                      ? "Changing it breaks links coaches saved."
                      : `/my-coaching/business/courses/${values.slug.trim() || "…"}`
                  }
                  label="Address"
                  required
                />
              )}
            </form.AppField>
          </div>
          <form.AppField name="headline">
            {(field) => (
              <field.TextField
                helpText="The heading at the top of the course page."
                label="Headline"
                required
              />
            )}
          </form.AppField>
          <form.AppField name="description">
            {(field) => (
              <field.TextareaField
                helpText="One or two sentences under the headline: what the course is, and that it is included in NOVA."
                label="Lead"
                rows={3}
              />
            )}
          </form.AppField>
          <Button loading={save.isPending} type="submit" variant="primary">
            Save details
          </Button>
        </form>
      </FieldErrorsProvider>
    </section>
  );
}

function WeekList({ course }: { course: AdminCourse }) {
  const invalidate = useInvalidateLearning();
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState<AdminCourseWeek | null>(null);
  const { data: videos = [] } = useQuery(videosQueryOptions());
  const addForm = useAppForm({ defaultValues: { title: "" } });
  const newTitle = useStore(addForm.store, (state) => state.values.title);

  const reorder = useMutation({
    mutationFn: (weekIds: string[]) =>
      reorderCourseWeeksFn({ data: { courseId: course.id, weekIds } }),
    onSuccess: invalidate,
    onError: () => toast.error("Couldn't move the week. Reload and try again."),
  });

  const add = useMutation({
    mutationFn: (title: string) =>
      createCourseWeekFn({ data: { courseId: course.id, title } }),
    onSuccess: async (week) => {
      addForm.reset();
      await invalidate();
      await navigate({
        to: "/admin/courses/$courseId/weeks/$weekId",
        params: { courseId: course.id, weekId: week.id },
      });
    },
    onError: () => toast.error("Couldn't add the week."),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteCourseWeekFn({ data: { id } }),
    onSuccess: async () => {
      setDeleting(null);
      await invalidate();
      toast.success("Week deleted. The weeks after it moved up.");
    },
    onError: () => toast.error("Couldn't delete the week."),
  });

  const move = (index: number, by: -1 | 1): void => {
    const ids = course.weeks.map((week) => week.id);
    const [moved] = ids.splice(index, 1);
    ids.splice(index + by, 0, moved);
    reorder.mutate(ids);
  };

  return (
    <section className="space-y-4">
      <div className="space-y-1">
        <h2 className="font-medium text-gray-900">Weeks</h2>
        <p className="text-text-secondary text-sm">
          Coaches see them in this order. A coach in her trial or on a paid plan
          opens each one by finishing the one before, either by approving what
          it builds in NOVA or by marking it complete.
        </p>
      </div>

      {course.weeks.length > 0 && (
        <ol className="divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white">
          {course.weeks.map((week, index) => {
            const classVideo = videos.find(
              (video) => video.id === week.classVideoId,
            );
            const weekLink = {
              params: { courseId: course.id, weekId: week.id },
              to: "/admin/courses/$courseId/weeks/$weekId",
            } as const;
            return (
              <li
                className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3"
                key={week.id}
              >
                <span className="text-text-tertiary w-16 flex-none text-sm tabular-nums">
                  Week {week.position}
                </span>
                <div className="min-w-0 flex-1 basis-56 space-y-0.5">
                  <Link
                    className="font-medium text-gray-900 hover:text-purple-700"
                    {...weekLink}
                  >
                    {week.title}
                  </Link>
                  <p className="text-text-tertiary text-xs">
                    {[
                      week.materials.length === 1
                        ? "1 material"
                        : `${week.materials.length} materials`,
                      week.producesAssets.length > 0
                        ? `Builds: ${week.producesAssets.map((key) => assetByKey(key).label).join(", ")}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                </div>
                {week.classVideoId === null ? (
                  <span className="w-64 text-xs">
                    <span className="text-amber-800">No class video</span>
                    <span aria-hidden="true" className="text-text-tertiary">
                      {" · "}
                    </span>
                    <Link
                      className="text-purple-700 underline-offset-2 hover:underline"
                      {...weekLink}
                    >
                      Choose one
                    </Link>
                  </span>
                ) : classVideo ? (
                  <span className="flex w-64 min-w-0 items-center gap-2">
                    <VideoThumbnail className="w-14" video={classVideo} />
                    <span className="text-text-secondary line-clamp-2 text-xs">
                      {classVideo.title}
                    </span>
                  </span>
                ) : (
                  // Set, but not in the loaded library list (still loading or failed).
                  <span className="text-text-secondary w-64 text-xs">
                    Class video
                  </span>
                )}
                <div className="flex gap-1">
                  <Button
                    aria-label={`Move week ${week.position} up`}
                    disabled={index === 0 || reorder.isPending}
                    onClick={() => move(index, -1)}
                    size="icon-sm"
                    variant="ghost"
                  >
                    <ArrowUp className="size-4" />
                  </Button>
                  <Button
                    aria-label={`Move week ${week.position} down`}
                    disabled={
                      index === course.weeks.length - 1 || reorder.isPending
                    }
                    onClick={() => move(index, 1)}
                    size="icon-sm"
                    variant="ghost"
                  >
                    <ArrowDown className="size-4" />
                  </Button>
                  <Button
                    onClick={() => setDeleting(week)}
                    size="sm"
                    variant="destructive-ghost"
                  >
                    Delete
                  </Button>
                </div>
              </li>
            );
          })}
        </ol>
      )}

      <form
        className="flex flex-wrap items-start gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          const title = newTitle.trim();
          if (title) add.mutate(title);
        }}
      >
        <div className="max-w-sm min-w-0 flex-1 basis-64">
          <addForm.Field name="title">
            {(field) => (
              <Input
                aria-label="New week's title"
                className="mb-0"
                onBlur={field.handleBlur}
                onValueChange={field.handleChange}
                placeholder={`Week ${course.weeks.length + 1} title`}
                value={field.state.value}
              />
            )}
          </addForm.Field>
        </div>
        <Button
          disabled={newTitle.trim() === ""}
          loading={add.isPending}
          type="submit"
        >
          Add week
        </Button>
      </form>

      <ConfirmDialog
        confirmLabel="Delete week"
        description="Its materials and every coach's completion of it go too. The weeks after it move up one."
        isLoading={remove.isPending}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting.id);
        }}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        open={deleting !== null}
        title={`Delete week ${deleting?.position ?? ""}: “${deleting?.title ?? ""}”?`}
        variant="destructive"
      />
    </section>
  );
}
